<script setup lang="js">
import { unref } from 'vue'
import { useLocale } from '@/tiptap/locales/index.js'
import { isFunction } from '@/tiptap/utils/utils'

// Optional: import a base-kit extension so the toolbar can read its bubble/toolbar config
// If you don't have this extension wired elsewhere, the guard below will simply skip it.
// (No direct usage here; we only read extension.options.button via the editor instance.)

const props = defineProps({
  editor: { type: Object, required: true },
})

const { t } = useLocale()

function getMenus() {
  if (!props.editor || !props.editor.extensionManager) return []
  const extensions = [...(props.editor.extensionManager.extensions || [])]

  const sortExtensions = extensions.sort((a, b) => {
    const A = (a && a.options && a.options.sort) ?? -1
    const B = (b && b.options && b.options.sort) ?? -1
    return A - B
  })

  return sortExtensions.reduce((acc, extension) => {
    const opts = extension && extension.options ? extension.options : {}
    const button = opts.button
    const divider = opts.divider === true
    const spacer = opts.spacer === true

    if (!button || !isFunction(button)) return acc

    const result = button({
      editor: props.editor,
      extension,
      t: unref(t),
    })

    if (Array.isArray(result)) {
      const menu = result.map((k, i) => ({
        button: k,
        divider: i === result.length - 1 ? divider : false,
        spacer: i === 0 ? spacer : false,
      }))
      return acc.concat(menu)
    }

    return acc.concat([{ button: result, divider, spacer }])
  }, [])
}
</script>

<template>
  <VToolbar v-bind="$attrs" density="compact" flat height="auto" class="py-1 ps-1">
    <template v-for="(item, key) in getMenus()" :key="key">
      <!-- Spacer -->
      <VSpacer v-if="item.spacer" />

      <!-- Buttons: guard against undefined structures -->
      <component
        v-if="item && item.button && item.button.component"
        :is="item.button.component"
        v-bind="item.button.componentProps"
        :editor="editor"
      >
        <template
          v-for="(element, slotName, i) in (item.button.componentSlots || {})"
          :key="i"
          #[slotName]="values"
        >
          <component :is="element" v-bind="values?.props" />
        </template>
      </component>

      <!-- Divider -->
      <VDivider v-if="item && item.divider" vertical class="mx-1 me-2" />
    </template>
  </VToolbar>
</template>